import type { IsoDate } from './calendar';
import type { Rate, SupplyKind } from './invoice';
import { type DocumentSeries, documentSeries } from './invoice-number';
import type { Paise } from './money';

/**
 * The GSTR-1 tables built from a month's sales: 4A (B2B invoices), 5 (B2C large invoices), 7 (other
 * B2C sales, in totals by place of supply and rate), 8 (nil-rated, exempt and non-GST sales), 9B (credit
 * and debit notes), 12 (HSN summary, B2B and B2C apart) and 13 (documents issued). Exports, amendments,
 * e-commerce sales (Table 14) and advances come later.
 *
 * Credit and debit notes (gap 4) carry positive amounts; their type gives the sign. A note goes where
 * its invoice went (`kind`): on a B2B invoice, Table 9B under the buyer's GSTIN; on a large B2C invoice,
 * Table 9B for unregistered buyers; on any other consumer bill it isn't listed on its own but nets into
 * that bill's Table 7 row, which may then go below zero.
 *
 * Lines at 0% are nil-rated: they're in Table 8 (and Table 12 at rate 0), not in Tables 4A, 5, 7 or 9B.
 * An invoice with only 0% lines is listed in Table 8 and Table 13 alone.
 *
 * Walk-in sales (Phase 16) come as one consumer entry with a line per rate and no HSN code: they're in
 * Table 7 and, by their bill numbers and the cancelled ones, Table 13, but not Table 12, whose B2C part
 * is optional up to ₹5 crore a year (the portal warns, it doesn't block).
 */

/** An invoice, or a credit or debit note against one or more invoices. */
export const DOC_TYPES = ['invoice', 'credit', 'debit'] as const;
export type DocType = (typeof DOC_TYPES)[number];

export interface Gstr1Line {
  /** Empty for walk-in totals, which aren't split by item: left out of Table 12. */
  hsn: string;
  description?: string | null;
  /** Unit quantity code, e.g. NOS, KGS; NA for services. */
  uqc: string;
  /** For a note, the quantity returned (0 when only the price changed). */
  quantity: number;
  rate: Rate;
  taxableValue: Paise;
  igst: Paise;
  cgst: Paise;
  sgst: Paise;
  cess: Paise;
}

export interface Gstr1Invoice {
  /** One number, or several for consumer bills added together. */
  numbers: readonly string[];
  date: IsoDate;
  /** Where it goes. For a note, where its invoice went. */
  kind: SupplyKind;
  /** An invoice unless it says otherwise. */
  docType?: DocType;
  buyerGstin: string | null;
  /** State code where the supply is taxed. */
  placeOfSupply: string;
  interState: boolean;
  invoiceValue: Paise;
  lines: readonly Gstr1Line[];
  /** Bills cancelled (walk-in sales): counted in Table 13 only. */
  cancelled?: readonly string[];
}

export interface Amounts {
  taxableValue: Paise;
  igst: Paise;
  cgst: Paise;
  sgst: Paise;
  cess: Paise;
}

export interface KindTotals extends Amounts {
  /** Invoices (for consumer bills added together, each bill). */
  count: number;
  invoiceValue: Paise;
}

/** Credit or debit notes, added up: always positive. */
export interface NoteTotals extends Amounts {
  count: number;
  /** The notes' own values (taxable value plus tax). */
  value: Paise;
}

export interface B2csRow extends Amounts {
  placeOfSupply: string;
  rate: Rate;
  interState: boolean;
}

export interface HsnRow extends Amounts {
  hsn: string;
  description: string | null;
  uqc: string;
  quantity: number;
  rate: Rate;
  /** Taxable value plus tax. */
  totalValue: Paise;
}

/** Table 8's rows: to registered or unregistered buyers, between states or within one. */
export const NIL_SUPPLY_TYPES = ['INTRB2B', 'INTRAB2B', 'INTRB2C', 'INTRAB2C'] as const;
export type NilSupplyType = (typeof NIL_SUPPLY_TYPES)[number];

/** Nil-rated, exempt and non-GST sales, in paise. */
export interface NilAmounts {
  nil: Paise;
  exempt: Paise;
  nonGst: Paise;
}

/** The GST-free sales a person gives for a month (Table 8), by row; 0% invoice lines are added to them. */
export type GstFreeAmounts = Partial<Record<NilSupplyType, Partial<NilAmounts>>>;

export interface NilRow extends NilAmounts {
  supplyType: NilSupplyType;
}

export const nilSupplyType = (registered: boolean, interState: boolean): NilSupplyType =>
  registered ? (interState ? 'INTRB2B' : 'INTRAB2B') : interState ? 'INTRB2C' : 'INTRAB2C';

export interface Gstr1Summary {
  /** Table 4A: invoices to registered buyers (their taxed lines). */
  b2b: KindTotals;
  /** Table 5: large invoices to consumers in other states. */
  b2cl: KindTotals;
  /** Table 7: other consumer sales, net of the notes on them (`credit`, `debit`: what was netted). */
  b2cs: KindTotals & { rows: B2csRow[]; credit: NoteTotals; debit: NoteTotals };
  /**
   * Table 9B: notes listed on their own (on B2B and large B2C invoices). `cdnr` and `cdnur` are each
   * part's notes, net (debit notes less credit notes), as the portal's summary adds them up.
   */
  notes: {
    credit: NoteTotals;
    debit: NoteTotals;
    registered: number;
    unregistered: number;
    cdnr: NoteTotals;
    cdnur: NoteTotals;
  };
  /** Table 8: GST-free sales; `fromBills` is the nil-rated part that came from 0% invoice lines. */
  nil: { rows: NilRow[]; total: NilAmounts; fromBills: Paise };
  hsn: { b2b: HsnRow[]; b2c: HsnRow[] };
  /** Table 13: invoices, credit notes and debit notes, each their own series. */
  documents: DocumentSeries[];
  creditNotes: DocumentSeries[];
  debitNotes: DocumentSeries[];
  /** Everything taxed, net of the notes: count is invoices (not notes). */
  totals: KindTotals & { tax: Paise };
}

const zero = (): Amounts => ({ taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 });
const noNotes = (): NoteTotals => ({ ...zero(), count: 0, value: 0 });

function add(into: Amounts, from: Amounts, sign = 1) {
  into.taxableValue += sign * from.taxableValue;
  into.igst += sign * from.igst;
  into.cgst += sign * from.cgst;
  into.sgst += sign * from.sgst;
  into.cess += sign * from.cess;
}

const tax = (a: Amounts) => a.igst + a.cgst + a.sgst + a.cess;
const tidyQuantity = (q: number) => Math.round(q * 1000) / 1000;
export const isNilRated = (line: { rate: Rate }) => line.rate === 0;
export const docTypeOf = (invoice: { docType?: DocType }): DocType => invoice.docType ?? 'invoice';

/** The month's credit and debit notes (gap 4): Table 9B's, and those netted into consumer sales (Table 7). */
export const notesIn = (summary: Pick<Gstr1Summary, 'notes' | 'b2cs'>): number =>
  summary.notes.credit.count + summary.notes.debit.count + summary.b2cs.credit.count + summary.b2cs.debit.count;

/** GST-free sales given for the month (Table 8), apart from the 0% lines of its bills. */
export const gstFreeIn = (summary: Pick<Gstr1Summary, 'nil'>): Paise =>
  summary.nil.total.nil + summary.nil.total.exempt + summary.nil.total.nonGst - summary.nil.fromBills;

export function gstr1Summary(invoices: readonly Gstr1Invoice[], gstFree: GstFreeAmounts = {}): Gstr1Summary {
  const kinds: Record<SupplyKind, KindTotals> = {
    b2b: { ...zero(), count: 0, invoiceValue: 0 },
    b2cl: { ...zero(), count: 0, invoiceValue: 0 },
    b2cs: { ...zero(), count: 0, invoiceValue: 0 },
  };
  const b2csNotes = { credit: noNotes(), debit: noNotes() };
  const notes = {
    credit: noNotes(),
    debit: noNotes(),
    registered: 0,
    unregistered: 0,
    cdnr: noNotes(),
    cdnur: noNotes(),
  };
  const b2cs = new Map<string, B2csRow>();
  const hsn = { b2b: new Map<string, HsnRow>(), b2c: new Map<string, HsnRow>() };
  const nil = new Map<NilSupplyType, NilAmounts>();
  const numbers: string[] = [];
  const cancelled: string[] = [];
  const noteNumbers = { credit: [] as string[], debit: [] as string[] };
  let invoiceCount = 0;
  let fromBills = 0;

  const nilRow = (type: NilSupplyType) => {
    const row = nil.get(type) ?? { nil: 0, exempt: 0, nonGst: 0 };
    nil.set(type, row);
    return row;
  };

  for (const invoice of invoices) {
    const docType = docTypeOf(invoice);
    const sign = docType === 'credit' ? -1 : 1;
    const taxed = invoice.lines.filter((line) => !isNilRated(line));
    const kind = kinds[invoice.kind];
    if (docType === 'invoice') {
      const bills = invoice.kind === 'b2cs' ? Math.max(1, invoice.numbers.length) : 1;
      invoiceCount += bills;
      numbers.push(...invoice.numbers);
      cancelled.push(...(invoice.cancelled ?? []));
      if (taxed.length) {
        kind.count += bills;
        kind.invoiceValue += invoice.invoiceValue;
      }
    } else {
      noteNumbers[docType].push(...invoice.numbers);
      const listed = invoice.kind !== 'b2cs';
      const into = listed ? notes[docType] : b2csNotes[docType];
      into.count += 1;
      for (const line of taxed) {
        add(into, line);
        into.value += line.taxableValue + tax(line);
      }
      if (listed && taxed.length) {
        notes[invoice.kind === 'b2b' ? 'registered' : 'unregistered'] += 1;
        const part = notes[invoice.kind === 'b2b' ? 'cdnr' : 'cdnur'];
        part.count += 1;
        for (const line of taxed) {
          add(part, line, sign);
          part.value += sign * (line.taxableValue + tax(line));
        }
      }
    }

    for (const line of invoice.lines) {
      if (isNilRated(line)) {
        const row = nilRow(nilSupplyType(invoice.kind === 'b2b', invoice.interState));
        row.nil += sign * line.taxableValue;
        fromBills += sign * line.taxableValue;
      } else if (docType === 'invoice' || invoice.kind === 'b2cs') {
        add(kind, line, sign);
        if (invoice.kind === 'b2cs') {
          const key = `${invoice.placeOfSupply}|${line.rate}|${invoice.interState}`;
          const row = b2cs.get(key) ?? {
            ...zero(),
            placeOfSupply: invoice.placeOfSupply,
            rate: line.rate,
            interState: invoice.interState,
          };
          add(row, line, sign);
          b2cs.set(key, row);
        }
        if (docType !== 'invoice') kind.invoiceValue += sign * (line.taxableValue + tax(line));
      }
      if (!line.hsn) continue;
      const table = invoice.kind === 'b2b' ? hsn.b2b : hsn.b2c;
      const key = `${line.hsn}|${line.uqc}|${line.rate}`;
      const row = table.get(key) ?? {
        ...zero(),
        hsn: line.hsn,
        description: line.description ?? null,
        uqc: line.uqc,
        quantity: 0,
        rate: line.rate,
        totalValue: 0,
      };
      add(row, line, sign);
      row.quantity = tidyQuantity(row.quantity + sign * line.quantity);
      row.totalValue += sign * (line.taxableValue + tax(line));
      if (!row.description && line.description) row.description = line.description;
      table.set(key, row);
    }
  }

  for (const type of NIL_SUPPLY_TYPES) {
    const given = gstFree[type];
    if (!given) continue;
    const row = nilRow(type);
    row.nil += given.nil ?? 0;
    row.exempt += given.exempt ?? 0;
    row.nonGst += given.nonGst ?? 0;
  }
  const nilRows: NilRow[] = NIL_SUPPLY_TYPES.flatMap((supplyType) => {
    const row = nil.get(supplyType);
    return row && (row.nil || row.exempt || row.nonGst) ? [{ supplyType, ...row }] : [];
  });
  const nilTotal = nilRows.reduce(
    (t, r) => ({ nil: t.nil + r.nil, exempt: t.exempt + r.exempt, nonGst: t.nonGst + r.nonGst }),
    { nil: 0, exempt: 0, nonGst: 0 },
  );

  const totals = { ...zero(), count: invoiceCount, invoiceValue: 0 };
  for (const kind of Object.values(kinds)) {
    add(totals, kind);
    totals.invoiceValue += kind.invoiceValue;
  }
  add(totals, notes.credit, -1);
  add(totals, notes.debit);
  totals.invoiceValue += notes.debit.value - notes.credit.value;

  const byHsn = (a: HsnRow, b: HsnRow) => a.hsn.localeCompare(b.hsn) || a.rate - b.rate || a.uqc.localeCompare(b.uqc);
  return {
    b2b: kinds.b2b,
    b2cl: kinds.b2cl,
    b2cs: {
      ...kinds.b2cs,
      rows: [...b2cs.values()].sort(
        (a, b) =>
          a.placeOfSupply.localeCompare(b.placeOfSupply) ||
          a.rate - b.rate ||
          Number(a.interState) - Number(b.interState),
      ),
      credit: b2csNotes.credit,
      debit: b2csNotes.debit,
    },
    notes,
    nil: { rows: nilRows, total: nilTotal, fromBills },
    hsn: { b2b: [...hsn.b2b.values()].sort(byHsn), b2c: [...hsn.b2c.values()].sort(byHsn) },
    documents: documentSeries(numbers, cancelled),
    creditNotes: documentSeries(noteNumbers.credit),
    debitNotes: documentSeries(noteNumbers.debit),
    totals: { ...totals, tax: tax(totals) },
  };
}
